/**
 * YearAccessModal - One-time $50 payment for 1 year of access (non-renewing).
 *
 * Replaces the recurring-subscription onboarding. Self-contained and isolated so it
 * cannot affect the existing subscription modal. Calls POST /api/subscriptions/purchase-year
 * (and /purchase-year/confirm for 3-D Secure). On success, access is granted server-side
 * with a 365-day period; this component just refreshes access state and calls onComplete.
 */

import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { clearAccessLevelCache } from '@/hooks/useAccessLevel'
import { usePreviousCard, formatCardBrand } from '@/hooks/usePreviousCard'
import { LEGAL } from '@/config/legalConfig'
import { CheckoutAgreement, OfferChecklist, buildAgreement, AGREEMENT_REQUIRED } from '../legal/CheckoutAgreement'
import { PurchaseConfirmation, accessEnd, type PaidInfo } from '../legal/PurchaseConfirmation'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!)
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// Display price — keep in sync with backend YEAR_ACCESS_PRICE
const PRICE_DISPLAY = '$50'

interface YearAccessModalProps {
  isOpen: boolean
  onComplete: () => void
  onClose?: () => void
}

function YearAccessForm({ onPaid }: { onPaid: (info: PaidInfo) => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [agreed, setAgreed] = useState(false)

  // Display-only hint: if a saved card exists, frame the (required) card
  // re-entry as a quick security re-verification. Does NOT change the $50
  // charge logic below — the entered card is still tokenized and charged
  // on the new Stripe account exactly as before.
  const { card: previousCard } = usePreviousCard(true)
  const hasPreviousCard = !!previousCard?.last4
  const previousBrand = formatCardBrand(previousCard?.brand)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!agreed) {
      setError(AGREEMENT_REQUIRED)
      return
    }
    if (!stripe || !elements) return

    setSubmitting(true)
    setError('')

    try {
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) throw new Error('Card form is not ready. Please try again.')

      // Tokenize the card (uses the publishable key — new Stripe account)
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement
      })
      if (pmError || !paymentMethod) {
        throw new Error(pmError?.message || 'Could not read your card. Please re-enter it.')
      }

      const token = localStorage.getItem('auth_token')
      const authHeaders = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }

      // 1) Charge $50 once
      const res = await fetch(`${API_URL}/api/subscriptions/purchase-year`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ paymentMethodId: paymentMethod.id, agreement: buildAgreement(LEGAL.offer.chargeLine) })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Payment failed. Please try again.')
      let accessUntil: string | undefined = data.accessUntil

      // 2) Handle 3-D Secure if the card requires it
      if (data.status === 'requires_action' && data.clientSecret) {
        const { error: actionError } = await stripe.handleCardAction(data.clientSecret)
        if (actionError) throw new Error(actionError.message || 'Card authentication failed.')

        const confirmRes = await fetch(`${API_URL}/api/subscriptions/purchase-year/confirm`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ paymentIntentId: data.paymentIntentId })
        })
        const confirmData = await confirmRes.json().catch(() => ({}))
        if (!confirmRes.ok) {
          throw new Error(confirmData.error || 'Payment authenticated but could not be confirmed. Please contact support.')
        }
        accessUntil = confirmData.accessUntil || accessUntil
      }

      // Success — refresh cached access so the app unlocks immediately
      clearAccessLevelCache()
      onPaid({
        plan: LEGAL.offer.name,
        amount: `${LEGAL.offer.price}.00`,
        paidAt: new Date(),
        renewal: 'No auto renewal',
        endLabel: 'Access Expires',
        endsAt: accessEnd(accessUntil, LEGAL.offer.days),
        card: paymentMethod.card ? { brand: paymentMethod.card.brand, last4: paymentMethod.card.last4 } : null,
        welcome: LEGAL.offer.welcome,
      })
    } catch (err: any) {
      setError(err?.message || 'Payment failed. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Header — emphasis on the full YEAR of access */}
      <div className="text-center">
        <div className="text-8xl mb-3">🎨</div>
        <h2 className="text-2xl font-bold text-gray-900">1 Full Year of Access</h2>
        <p className="text-gray-600 mt-1">
          Full access to everything for a whole year. One time payment of {PRICE_DISPLAY}, no auto renewal.
        </p>
        <OfferChecklist />
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Card */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {hasPreviousCard ? 'Re-verify your card' : 'Payment Information'}
        </label>
        {hasPreviousCard && (
          <div className="mb-2 rounded-lg p-3 bg-braydin-cream border border-braydin-sand">
            <span className="inline-flex items-center rounded-md bg-white border border-braydin-sand px-2 py-0.5 text-xs font-semibold text-braydin-olive">
              {previousBrand} ••{previousCard!.last4}
            </span>
            <p className="text-xs text-braydin-ink/70 mt-1">
              For your security, please re-enter your {previousBrand} card ending in ••{previousCard!.last4} to continue.
            </p>
          </div>
        )}
        <div className="border border-gray-300 rounded-lg p-3">
          <CardElement options={{ hidePostalCode: false }} />
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || submitting || !agreed}
        className="w-full px-6 py-3 bg-[#6b7850] text-white font-semibold rounded-lg hover:bg-[#5c5b44] transition-colors disabled:opacity-60"
      >
        {submitting ? 'Processing...' : `Get 1 Full Year of Access — ${PRICE_DISPLAY}`}
      </button>

      {/* Agreement: required before paying, recorded with the payment */}
      <CheckoutAgreement checked={agreed} onChange={(v) => { setAgreed(v); if (v) setError('') }} chargeLine={LEGAL.offer.chargeLine} />
    </form>
  )
}

export function YearAccessModal({ isOpen, onComplete, onClose }: YearAccessModalProps) {
  // After a successful payment: the confirmation screen, then onComplete
  const [paid, setPaid] = useState<PaidInfo | null>(null)
  if (paid) {
    return (
      <Modal isOpen={isOpen} onClose={() => {}} showCloseButton={false}>
        <PurchaseConfirmation info={paid} onContinue={onComplete} />
      </Modal>
    )
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose || (() => {})} showCloseButton={!!onClose}>
      <Elements stripe={stripePromise}>
        <YearAccessForm onPaid={setPaid} />
      </Elements>
    </Modal>
  )
}

export default YearAccessModal
